import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Copy, ArrowRightCircle, Download, Eye, Mail } from 'lucide-react';
import PdfViewerModal from './PdfViewerModal';
import EmailSendModal from './EmailSendModal';
import QuoteForm from './QuoteForm';
import QuoteViewModal from './QuoteViewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { brouillon: 'bg-gray-100 text-gray-600', envoye: 'bg-blue-50 text-blue-600', accepte: 'bg-green-50 text-green-600', refuse: 'bg-red-50 text-red-600', expire: 'bg-amber-50 text-amber-600' };
const STATUS_LIST = ['brouillon', 'envoye', 'accepte', 'refuse', 'expire'];

const emptyItem = { type: 'service', description: '', quantity: 1, unit_price: 0, tva_rate: 20 };
const emptyForm = { client_id: '', vehicle_id: '', items: [{ ...emptyItem }], discount: 0, notes: '', conditions: 'Devis valable 30 jours', status: 'brouillon' };

export default function QuotesModule({ authHeaders, refreshStats }) {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');
  const [showQuickClient, setShowQuickClient] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [pdfView, setPdfView] = useState(null);
  const [emailModal, setEmailModal] = useState(null);

  const fetchData = useCallback(async () => {
    const [qRes, cRes, vRes, sRes] = await Promise.all([
      axios.get(`${API_URL}/api/crm/quotes`, { headers: authHeaders, params: filter ? { status: filter } : {} }),
      axios.get(`${API_URL}/api/crm/clients`, { headers: authHeaders }),
      axios.get(`${API_URL}/api/crm/client-vehicles`, { headers: authHeaders }),
      axios.get(`${API_URL}/api/crm/settings`, { headers: authHeaders })
    ]);
    setQuotes(qRes.data); setClients(cRes.data); setVehicles(vRes.data); setCompany(sRes.data);
  }, [filter, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) await axios.put(`${API_URL}/api/crm/quotes/${editing.id}`, form, { headers: authHeaders });
      else await axios.post(`${API_URL}/api/crm/quotes`, form, { headers: authHeaders });
      setShowForm(false); setEditing(null); fetchData(); refreshStats?.();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer ce devis ?')) {
      await axios.delete(`${API_URL}/api/crm/quotes/${id}`, { headers: authHeaders });
      fetchData(); refreshStats?.();
    }
  };

  const handleDuplicate = async (id) => { await axios.post(`${API_URL}/api/crm/quotes/${id}/duplicate`, {}, { headers: authHeaders }); fetchData(); };
  const handleConvert = async (id) => { if (window.confirm('Convertir ce devis en facture ?')) { await axios.post(`${API_URL}/api/crm/quotes/${id}/convert`, {}, { headers: authHeaders }); fetchData(); refreshStats?.(); }};

  const openPDF = (id, number) => {
    const token = sessionStorage.getItem('mca_auth_token');
    if (!token) { alert('Session expirée, veuillez vous reconnecter'); return; }
    setPdfView({ url: `${API_URL}/api/crm/quotes/${id}/pdf?token=${token}`, number: number || id });
  };

  const openEdit = async (id) => {
    const res = await axios.get(`${API_URL}/api/crm/quotes/${id}`, { headers: authHeaders });
    const q = res.data;
    setForm({ client_id: q.client_id, vehicle_id: q.vehicle_id || '', items: q.items?.length ? q.items : [{ ...emptyItem }], discount: q.discount || 0, notes: q.notes || '', conditions: q.conditions || '', status: q.status });
    setEditing(q); setShowForm(true);
  };

  const openView = async (id) => {
    const res = await axios.get(`${API_URL}/api/crm/quotes/${id}`, { headers: authHeaders });
    setViewing(res.data);
  };

  const openNew = () => {
    setForm({ ...emptyForm, items: [{ ...emptyItem }] });
    setEditing(null);
    setShowForm(true);
  };

  return (
    <div data-testid="quotes-module">
      {/* Filters + Add button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!filter ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Tous</button>
          {STATUS_LIST.map(s => <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${filter === s ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{s}</button>)}
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700" data-testid="add-quote-btn"><Plus size={16} /> Nouveau devis</button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="text-left p-3">N°</th><th className="text-left p-3">Client</th><th className="text-left p-3 hidden md:table-cell">Date</th><th className="text-right p-3">Total TTC</th><th className="text-center p-3">Statut</th><th className="text-right p-3">Actions</th></tr></thead>
            <tbody>
              {quotes.map(q => (
                <tr key={q.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-mono font-medium">
                    <div className="flex items-center gap-1.5">
                      {q.number}
                      {q.email_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full" title={`Envoyé ${q.email_count} fois`} data-testid={`email-badge-quote-${q.id}`}>
                          <Mail size={9} /> {q.email_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">{q.client_name}</td>
                  <td className="p-3 hidden md:table-cell text-gray-500">{q.created_at?.slice(0, 10)}</td>
                  <td className="p-3 text-right font-semibold">{q.total?.toLocaleString('fr-FR')}€</td>
                  <td className="p-3 text-center"><span className={`text-xs px-2 py-1 rounded-full capitalize ${STATUS_COLORS[q.status] || ''}`}>{q.status}</span></td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openView(q.id)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Consulter" data-testid={`view-quote-${q.id}`}><Eye size={15} /></button>
                      <button onClick={() => openEdit(q.id)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Modifier"><Pencil size={15} /></button>
                      <button onClick={() => openPDF(q.id, q.number)} className="p-1.5 hover:bg-purple-50 rounded text-purple-600" title="PDF" data-testid={`pdf-quote-${q.id}`}><Download size={15} /></button>
                      <button onClick={() => setEmailModal({ id: q.id, number: q.number, clientName: q.client_name, clientEmail: q.client_email })} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Email" data-testid={`email-quote-${q.id}`}><Mail size={15} /></button>
                      <button onClick={() => handleDuplicate(q.id)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Dupliquer"><Copy size={15} /></button>
                      {q.status !== 'accepte' && <button onClick={() => handleConvert(q.id)} className="p-1.5 hover:bg-green-50 rounded text-green-600" title="Convertir en facture"><ArrowRightCircle size={15} /></button>}
                      <button onClick={() => handleDelete(q.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Supprimer"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!quotes.length && <tr><td colSpan={6} className="p-8 text-center text-gray-400">Aucun devis</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showForm && (
        <QuoteForm
          form={form} setForm={setForm}
          editing={editing} clients={clients} vehicles={vehicles}
          emptyItem={emptyItem}
          showQuickClient={showQuickClient} setShowQuickClient={setShowQuickClient}
          setClients={setClients}
          authHeaders={authHeaders}
          onSave={handleSave}
          onClose={() => setShowForm(false)}
        />
      )}

      {viewing && (
        <QuoteViewModal
          viewing={viewing}
          company={company}
          onClose={() => setViewing(null)}
          onEdit={() => { setViewing(null); openEdit(viewing.id); }}
          onPDF={() => openPDF(viewing.id, viewing.number)}
          onEmail={() => setEmailModal({ id: viewing.id, number: viewing.number, clientName: viewing.client_name, clientEmail: viewing.client_email })}
        />
      )}

      {pdfView && (
        <PdfViewerModal url={pdfView.url} title={pdfView.number} onClose={() => setPdfView(null)} />
      )}

      {emailModal && (
        <EmailSendModal
          docType="quote"
          docId={emailModal.id}
          docNumber={emailModal.number}
          clientName={emailModal.clientName}
          clientEmail={emailModal.clientEmail}
          authHeaders={authHeaders}
          onClose={() => setEmailModal(null)}
          onSent={() => { fetchData(); if (viewing) openView(viewing.id); }}
        />
      )}
    </div>
  );
}
